import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import tiReportService from '../../services/TIReportService.js';
import tiReportFileCabinetService from '../../services/TIReportFileCabinetService.js';
import userService from '../../services/UserService.js';
import { FileSearch, Plus, Calendar, Filter, Download, Eye, CheckCircle, XCircle, Pencil, Trash, Mail, Copy } from "lucide-react";
import { hasPermission, ROLES } from '../../utils/permissions.js';
import { getListActionButtons, isPaidRecord } from '../../utils/roleBasedUIHelpers.js';

const TIReportList = () => {
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clientFilter, setClientFilter] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const perPage = 10;

  const userRole = user?.role_name || user?.role;
  const isClient = userRole === ROLES.CLIENT;
  const isAccountant = userRole === ROLES.ACCOUNTANT;
  const isViewOnlyRole = isClient || isAccountant;

  const canCreate = hasPermission(userRole, 'tiReport', 'create');
  const canSendEmail = hasPermission(userRole, 'tiReport', 'sendEmail');
  const canDownloadFileCabinet = hasPermission(userRole, 'tiReport', 'downloadFileCabinet');

  const actionButtons = getListActionButtons(userRole);

  // Check if all selected reports are published (for bulk PDF download - client only restriction)
  const allSelectedPublished = selectedReports.length > 0 &&
    (!isClient || selectAllPages || selectedReports.every(id => reports.find(r => r.id === id)?.published));

  useEffect(() => {
    if (!isClient) {
      fetchClients();
    }
    fetchLocations();
  }, [isClient]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchReports();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, clientFilter, publishedFilter, locationFilter, paymentStatusFilter, fromDate, toDate, isClient]);

  useEffect(() => {
    fetchReports();
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selectAll state based on current page reports
  useEffect(() => {
    if (reports.length === 0) return;
    const allCurrentPageSelected = reports.every(r => selectedReports.includes(r.id));
    setSelectAll(allCurrentPageSelected);
  }, [reports, selectedReports]);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients();
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const locationService = (await import('../../services/LocationService.js')).default;
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const getPaymentStatusStyle = (paymentStatus) => {
    switch(paymentStatus) {
      case 'paid':
        return 'bg-green-50 border-green-300 text-green-700';
      case 'unpaid':
        return 'bg-red-50 border-red-300 text-red-700';
      case 'pending':
      default:
        return 'bg-yellow-50 border-yellow-300 text-yellow-700';
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (clientFilter) filters.client_id = clientFilter;
      if (publishedFilter) filters.published = publishedFilter;
      if (locationFilter) filters.location_id = locationFilter;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
      if (paymentStatusFilter) filters.payment_status = paymentStatusFilter;

      let response;
      if (isClient) {
        const clientId = user?.client_id || user?.user_id;
        if (!clientId) {
          Alert.error('Client ID not found. Please login again.');
          setLoading(false);
          return;
        }
        response = await tiReportService.getClientReports(clientId, currentPage, perPage, filters);
      } else {
        response = await tiReportService.getAll(currentPage, perPage, filters);
      }

      setReports(response.data);
      setTotalPages(response.pagination.pages);
      setTotalCount(response.pagination.total || 0);
    } catch (err) {
      Alert.error('Failed to load TI reports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = async () => {
    if (selectAll || selectAllPages) {
      setSelectedReports([]);
      setSelectAll(false);
      setSelectAllPages(false);
      return;
    }
    if (totalPages <= 1) {
      setSelectedReports(reports.map(r => r.id));
      setSelectAll(true);
      return;
    }
    try {
      setSelectAllLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (clientFilter) filters.client_id = clientFilter;
      if (publishedFilter) filters.published = publishedFilter;
      if (locationFilter) filters.location_id = locationFilter;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
      if (paymentStatusFilter) filters.payment_status = paymentStatusFilter;
      let response;
      if (isClient) {
        const clientId = user?.client_id || user?.user_id;
        response = await tiReportService.getClientReports(clientId, 1, totalCount || 9999, filters);
      } else {
        response = await tiReportService.getAll(null, null, filters);
      }
      const allIds = (response.data || []).map(r => r.id);
      setSelectedReports(allIds);
      setSelectAllPages(true);
      setSelectAll(true);
    } catch (err) {
      Alert.error('Failed to select all records');
      console.error(err);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleSelectReport = (reportId) => {
    if (selectedReports.includes(reportId)) {
      const newSelected = selectedReports.filter(id => id !== reportId);
      setSelectedReports(newSelected);
      setSelectAllPages(false);
      setSelectAll(reports.every(r => newSelected.includes(r.id)));
    } else {
      const newSelected = [...selectedReports, reportId];
      setSelectedReports(newSelected);
      setSelectAll(reports.every(r => newSelected.includes(r.id)));
    }
  };

  const handleDownloadCSV = async () => {
    if (selectedReports.length === 0) {
      Alert.warning('Please select at least one TI report to download');
      return;
    }
    try {
      const blob = await tiReportService.exportCSV(selectedReports);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `ti_reports_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success(`${selectedReports.length} report(s) exported successfully!`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to export CSV');
      console.error(err);
    }
  };

  const handleSendBulkEmail = async () => {
    if (selectedReports.length === 0) {
      Alert.warning('Please select at least one TI report to send email');
      return;
    }
    const confirmed = await Alert.confirm(
      `Are you sure you want to send email for ${selectedReports.length} selected report(s)?`,
      'Send Bulk Email'
    );
    if (confirmed) {
      try {
        await tiReportService.sendBulkEmail(selectedReports);
        Alert.success(`Email processing started for ${selectedReports.length} report(s)!`);
        setSelectedReports([]);
        setSelectAll(false);
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to send bulk email');
      }
    }
  };

  const handleDownloadBulkPDF = async () => {
    if (selectedReports.length === 0) {
      Alert.warning('Please select at least one TI report to download');
      return;
    }

    try {
      const response = await tiReportService.downloadBulkPDF(selectedReports);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition?.split('filename=')[1]?.replace(/"/g, '')
        || `ti_reports_${new Date().toISOString().split('T')[0]}.zip`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success(`${selectedReports.length} report(s) downloaded successfully!`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to download PDFs');
      console.error(err);
    }
  };

  const handleDownloadAllCabinet = async () => {
    try {
      const blob = await tiReportFileCabinetService.downloadAllCabinet();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `ti_report_file_cabinet_${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('File cabinet downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download file cabinet');
      console.error(err);
    }
  };

  const handleTogglePublished = async (reportId, currentStatus) => {
    try {
      await tiReportService.togglePublished(reportId);
      Alert.success(`TI report ${currentStatus ? 'unpublished' : 'published'} successfully!`);
      fetchReports();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update published status');
    }
  };

  const handleDelete = async (reportId, reportNo) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete TI report "${reportNo}"? This action cannot be undone.`,
      'Delete TI Report'
    );
    if (confirmed) {
      try {
        await tiReportService.delete(reportId);
        Alert.success('TI report deleted successfully!');
        if (reports.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchReports();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete TI report');
      }
    }
  };

  const handleDuplicate = async (reportId, tiId) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to duplicate TI report "${tiId}"? A new record will be created with the same data.`,
      'Duplicate TI Report'
    );
    if (confirmed) {
      try {
        await tiReportService.duplicate(reportId);
        Alert.success('TI report duplicated successfully!');
        fetchReports();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to duplicate TI report');
      }
    }
  };

  const handleDownloadCertificate = async (reportId, reportNo, tankNo) => {
    try {
      const blob = await tiReportService.generateCertificate(reportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ti_report_${(tankNo || reportNo).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Certificate downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download certificate');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setClientFilter('');
    setPublishedFilter('');
    setLocationFilter('');
    setPaymentStatusFilter('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client ? client.name : clientId;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">

              {/* Page Header */}
              <div className="max-w-8xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <FileSearch className="w-5 h-5 text-buttonBg" />
                      TI Reports
                    </h1>
                  </div>

                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {selectedReports.length > 0 && (
                      <>
                        <button
                          onClick={handleDownloadCSV}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          <Download size={18} />
                          <span className="hidden sm:inline">Download CSV ({selectedReports.length})</span>
                          <span className="sm:hidden">CSV ({selectedReports.length})</span>
                        </button>
                        {canSendEmail && (
                          <button
                            onClick={handleSendBulkEmail}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                          >
                            <Mail size={18} />
                            <span className="hidden sm:inline">Send Email ({selectedReports.length})</span>
                            <span className="sm:hidden">Email ({selectedReports.length})</span>
                          </button>
                        )}
                        <button
                          onClick={allSelectedPublished ? handleDownloadBulkPDF : undefined}
                          disabled={!allSelectedPublished}
                          title={!allSelectedPublished ? 'Only published records can be downloaded as PDF' : ''}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                            allSelectedPublished
                              ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer'
                              : 'bg-purple-300 text-white cursor-not-allowed opacity-60'
                          }`}
                        >
                          <Download size={18} />
                          <span className="hidden sm:inline">Download Reports ({selectedReports.length})</span>
                          <span className="sm:hidden">Reports ({selectedReports.length})</span>
                        </button>
                      </>
                    )}
                    {canDownloadFileCabinet && (
                      <button
                        onClick={handleDownloadAllCabinet}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        <Download size={18} />
                        <span className="hidden sm:inline">All Files</span>
                      </button>
                    )}
                    {canCreate && (
                      <button
                        onClick={() => navigate('/ti-reports/add')}
                        className="
                          relative overflow-hidden
                          group flex items-center justify-center gap-2
                          px-3 sm:px-3 py-2.5 sm:py-2
                          text-xs sm:text-sm
                          rounded-lg
                          border border-buttonBg
                          bg-buttonBg text-buttonTxt
                          font-medium whitespace-nowrap

                          transition-colors duration-300 ease-out
                          hover:bg-transparent hover:text-buttonBg
                        "
                      >
                        {/* COLLAPSING BACKGROUND */}
                        <span
                          className="
                            absolute inset-0
                            bg-buttonBg
                            scale-x-100
                            group-hover:scale-x-0
                            origin-center
                            transition-transform duration-300 ease-out
                            z-0
                          "
                        />

                        {/* ICON */}
                        <Plus
                          className="
                            w-4 h-4 relative z-10
                            transition-transform duration-300 ease-out
                            group-hover:scale-90
                          "
                        />

                        <span className="relative z-10">New TI Report</span>
                        <span className="sm:hidden">New</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                <div className="flex items-center justify-between mb-0 sm:mb-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-buttonBg font-bold"/>
                    <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                  </div>
                </div>
                <div className='hidden sm:block'>
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4'> 
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Search by TI ID, Tank No..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <button
                      onClick={() => setShowMobileFilters(!showMobileFilters)}
                      className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Filter size={18} />
                      Filters
                    </button>

                    {!isClient && (
                    <div className="hidden lg:flex gap-3 flex-wrap">
                        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                          className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                          <option value="">All Clients</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.first_name} {client.last_name}
                            </option>
                          ))}
                        </select>
                    </div>
                    )}
                    <div className="hidden lg:flex gap-3 flex-wrap">
                      <select value={publishedFilter} onChange={(e) => setPublishedFilter(e.target.value)}
                        className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option value="">All Status</option>
                        <option value="true">Published</option>
                        <option value="false">Unpublished</option>
                      </select>
                    </div>
                    <div className="hidden lg:flex gap-3 flex-wrap">
                      <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
                        className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option value="">All Locations</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>{loc.city}, {loc.state}</option>
                        ))}
                      </select>
                    </div>
                    <div className="hidden lg:flex gap-3 flex-wrap">
                      <select value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}
                        className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option value="">All Payment</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="unpaid">Unpaid</option>
                      </select>
                    </div>
                    <div className="hidden lg:flex gap-3 flex-wrap">
                      <div className='relative w-full'>
                        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                          className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                      </div>
                    </div>
                    <div className="hidden lg:flex gap-3 flex-wrap"> 
                      <div className='relative w-full'>
                      <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                        className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                        <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                      </div>
                      {(search || clientFilter || publishedFilter || locationFilter || paymentStatusFilter || fromDate || toDate) && (
                        <button onClick={clearFilters} className="px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm">
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mobile Filters */}
                  {showMobileFilters && (
                    <div className="lg:hidden mt-4 space-y-3 p-4 bg-gray-50 rounded-lg">
                      {!isClient && (
                        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                          <option value="">All Clients</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.first_name} {client.last_name}
                            </option>
                          ))}
                        </select>
                      )}
                      <select value={publishedFilter} onChange={(e) => setPublishedFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option value="">All Status</option>
                        <option value="true">Published</option>
                        <option value="false">Unpublished</option>
                      </select>
                      <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option value="">All Locations</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>{loc.city}, {loc.state}</option>
                        ))}
                      </select>
                      <select value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option value="">All Payment</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="unpaid">Unpaid</option>
                      </select>
                      <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                      <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                      {(search || clientFilter || publishedFilter || locationFilter || paymentStatusFilter || fromDate || toDate) && (
                        <button onClick={clearFilters} className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm">
                          Clear Filters
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-white rounded-sm shadow-sm">
                <div className="p-4 sm:p-6 lg:p-0">
                  {/* Table View - Desktop */}
                  <div className="hidden lg:block bg-white rounded-lg shadow-sm">
                    {loading ? (
                      <div className="p-12 text-center text-gray-600">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        Loading TI reports...
                      </div>
                    ) : reports.length === 0 ? (
                      <div className="p-12 text-center text-gray-600">No TI reports found</div>
                    ) : (
                      <>
                        <div className="hidden md:block w-full">
                                  <table className="w-full table-fixed border-collapse">
                                    <thead>
                                      <tr className="bg-primary text-tertiary">
                                  <th className="px-6 py-4 w-[4%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <input type="checkbox" checked={selectAll || selectAllPages} onChange={handleSelectAll}
                                      disabled={selectAllLoading}
                                      className={`w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 ${selectAllLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`} />
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TI ID</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tank No</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Depot Name</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Survey Date</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inspector</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Published</th>
                                <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectAllLoading && (
                                <tr>
                                  <td colSpan="9" className="px-4 py-2 bg-orange-50 text-center text-sm text-orange-800 border-b border-orange-200">
                                    <span className="inline-flex items-center gap-2">
                                      <svg className="animate-spin h-4 w-4 text-orange-600" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                                      Selecting all {totalCount} records...
                                    </span>
                                  </td>
                                </tr>
                              )}
                              {selectAllPages && !selectAllLoading && (
                                <tr>
                                  <td colSpan="9" className="px-4 py-2 bg-orange-50 text-center text-sm text-orange-800 border-b border-orange-200">
                                    All {selectedReports.length} records across all pages are selected.{' '}
                                    <button onClick={() => { setSelectedReports([]); setSelectAll(false); setSelectAllPages(false); }} className="font-semibold underline hover:text-orange-600">Clear selection</button>
                                  </td>
                                </tr>
                              )}
                              {reports.map((report) => (
                                <tr key={report.id}
                                  className={`hover:bg-gray-50 ${!isClient ? report.published ? 'bg-white' : 'bg-white' : ''}`}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      <input type="checkbox" checked={selectedReports.includes(report.id)}
                                        onChange={() => handleSelectReport(report.id)}
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer" />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className={`inline-block px-3 py-1.5 rounded-md border-2 text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${getPaymentStatusStyle(report.payment_status)}`}
                                      onClick={() => navigate(`/ti-reports/view/${report.id}`)}>
                                      {report.ti_id}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                      <div className="text-sm text-left text-gray-900">{getClientName(report.client_id)}</div>
                                    </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{report.tank_no || 'N/A'}</div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-left text-gray-900">
                                      {report.depot_name || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{formatDate(report.date_of_survey)}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{report.inspected_by_name}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      {report.published ? (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Published</span>
                                      ) : (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Unpublished</span>
                                      )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium relative">
                                      <button onClick={() => setActiveDropdown(activeDropdown === report.id ? null : report.id)}
                                        className="text-gray-400 hover:text-gray-600">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                        </svg>
                                      </button>
                                    {activeDropdown === report.id && (
                                      <div ref={dropdownRef}
                                        className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                        {actionButtons.view && (
                                          <button onClick={() => { navigate(`/ti-reports/view/${report.id}`); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center">
                                            <Eye size={16} className="text-buttonBg" />
                                            <span className="text-base capitalize">View Details</span>
                                          </button>
                                        )}
                                        {actionButtons.downloadPDF && (
                                          <button
                                            onClick={() => {
                                              if (isClient && !report.published) return;
                                              handleDownloadCertificate(report.id, report.ti_id, report.tank_no);
                                              setActiveDropdown(null);
                                            }}
                                            disabled={isClient && !report.published}
                                            className={`w-full px-4 py-2 text-left text-sm flex gap-2 items-center ${isClient && !report.published ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                          >
                                            <Download size={16} className="text-buttonBg" />
                                            <span className="text-base capitalize">Download Report</span>
                                          </button>
                                        )}
                                        {actionButtons.publish && (
                                          <button onClick={() => { handleTogglePublished(report.id, report.published); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center">
                                            {report.published ? <XCircle size={16} className="text-buttonBg" /> : <CheckCircle size={16} className="text-buttonBg" />}
                                            <span className="text-base capitalize">{report.published ? 'Unpublish' : 'Publish'}</span>
                                          </button>
                                        )}
                                        {actionButtons.edit && !isPaidRecord(report.payment_status) && (
                                          <button onClick={() => { navigate(`/ti-reports/edit/${report.id}`); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center">
                                            <Pencil size={16} className="text-buttonBg" />
                                            <span className="text-base capitalize">Edit</span>
                                          </button>
                                        )}
                                        {actionButtons.delete && !isPaidRecord(report.payment_status) && (
                                          <button onClick={() => { handleDelete(report.id, report.ti_id); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center">
                                            <Trash size={16} className="text-buttonBg" />
                                            <span className="text-base capitalize">Delete</span>
                                          </button>
                                        )}
                                        {!isViewOnlyRole && (
                                          <button onClick={() => { handleDuplicate(report.id, report.ti_id); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center">
                                            <Copy size={16} className="text-buttonBg" />
                                            <span className="text-base capitalize">Duplicate</span>
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                          <div className="text-sm text-gray-700">
                            Showing {reports.length} report{reports.length !== 1 ? 's' : ''}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition">
                              ← Previous
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition">
                              Next →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Card View - Mobile/Tablet */}
                  <div className="lg:hidden space-y-3 sm:space-y-4">
                    {loading ? (
                      <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        Loading TI reports...
                      </div>
                    ) : reports.length === 0 ? (
                      <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No TI reports found</div>
                    ) : (
                      <>
                        {reports.map((report) => (
                          <div key={report.id}
                            className={`rounded-lg shadow-sm p-4 sm:p-5 ${!isClient ? report.published ? 'bg-green-50' : 'bg-red-50' : 'bg-white'}`}>
                            <div className="flex items-start gap-3 sm:gap-4 mb-3">
                              {!isClient && (
                                <input type="checkbox" checked={selectedReports.includes(report.id)}
                                  onChange={() => handleSelectReport(report.id)}
                                  className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer" />
                              )}
                              <FileSearch className="w-10 h-10 text-tertiary flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-base sm:text-lg">
                                  <span className={`inline-block px-3 py-1.5 rounded-md border-2 text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${getPaymentStatusStyle(report.payment_status)}`}
                                    onClick={() => navigate(`/ti-reports/view/${report.id}`)}>
                                    {report.ti_id}
                                  </span>
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600 truncate mt-1">
                                  {!isClient && getClientName(report.client_id)}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                              <div>
                                <span className="text-gray-500">Tank No:</span>
                                <p className="text-gray-900 font-medium">{report.tank_no || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Depot Name:</span>
                                <p className="text-gray-900 font-medium">
                                  {report.depot_name || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Survey Date:</span>
                                <p className="text-gray-900 font-medium">{formatDate(report.date_of_survey)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Published:</span>
                                <p className={`font-medium ${report.published ? 'text-green-700' : 'text-red-700'}`}>
                                  {report.published ? 'Yes' : 'No'}
                                </p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-500">Inspector:</span>
                                <p className="text-gray-900 font-medium truncate">{report.inspected_by_name}</p>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <button onClick={() => navigate(`/ti-reports/view/${report.id}`)}
                                className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium">
                                👁️ View
                              </button>
                              <button
                                onClick={() => (!isClient || report.published) && handleDownloadCertificate(report.id, report.ti_id, report.tank_no)}
                                disabled={isClient && !report.published}
                                className={`flex-1 px-3 sm:px-4 py-2 border rounded-lg transition-colors text-xs sm:text-sm font-medium ${isClient && !report.published ? 'border-gray-200 text-gray-400 opacity-50 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                              >
                                📄 Report
                              </button>
                              {!isClient && !isPaidRecord(report.payment_status) && (
                                <>
                                  <button onClick={() => navigate(`/ti-reports/edit/${report.id}`)}
                                    className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium">
                                    ✏️ Edit
                                  </button>
                                  <button onClick={() => handleDelete(report.id, report.ti_id)}
                                    className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium">
                                    🗑️ Delete
                                  </button>
                                </>
                              )}
                              {!isViewOnlyRole && (
                                <button onClick={() => handleDuplicate(report.id, report.ti_id)}
                                  className="px-3 sm:px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-xs sm:text-sm font-medium">
                                  📋 Duplicate
                                </button>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Mobile Pagination */}
                        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                          <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                            Showing {reports.length} report{reports.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}
                              className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium">
                              ← Previous
                            </button>
                            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}
                              className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium">
                              Next →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TIReportList;
